import { TranslationType } from "./common";

export interface IError {
  extra: Record<string, unknown>;
  code: number;
  userMessage: TranslationType;
}

/**
 * Union type for all possible error formats in the application
 * - Standard JavaScript Error objects
 * - API error responses (IError)
 */
export type AppError = Error | IError;

/**
 * Type guard to check if an error is an IError (API error format)
 */
export function isApiError(error: unknown): error is IError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'userMessage' in error &&
    !('stack' in error) // Standard Error has stack, IError doesn't
  );
}

/**
 * The API's numeric error code, from either error shape.
 *
 * `isApiError` deliberately rejects anything carrying a `stack`, so it says
 * `false` for the output of `toApiError` — which is the shape every fetch
 * wrapper in this repo throws. Reading `code` through that guard therefore
 * misses the real errors; this reads it off both.
 */
export function getErrorCode(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('code' in error)) return undefined;
  const { code } = error as { code: unknown };
  return typeof code === 'number' ? code : undefined;
}

/**
 * Type guard to check if an error is a standard Error
 */
export function isStandardError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Extracts a human-readable error message from any error format
 * Priority:
 * 1. Standard Error.message
 * 2. IError.userMessage.original
 * 3. IError.extra.info
 * 4. The error itself (if it's a string)
 * 5. Stringified error as fallback
 */
export function getErrorMessage(error: unknown): string {
  if (!error) {
    return 'Unknown error';
  }

  // Standard Error object
  if (isStandardError(error)) {
    return error.message;
  }

  // Any object carrying an OBJECT-form userMessage — NOT gated on isApiError:
  // the BFF's generic error envelope (PRO-200) is { userMessage } with no
  // `code`, which isApiError rejects, and the message must still resolve.
  // Only original/en — a localized-only value must not fragment Sentry issue
  // grouping per language (the BFF's shaper guarantees `original` is set).
  // Deliberately does NOT short-circuit on a STRING userMessage — the
  // historical precedence lets extra.info (often more descriptive) win over
  // terse string keys, and GTM labels segment on it.
  if (typeof error === 'object' && 'userMessage' in error) {
    const userMessage = (error as { userMessage?: unknown }).userMessage;
    if (typeof userMessage === 'object' && userMessage !== null) {
      const t = userMessage as { original?: unknown; en?: unknown };
      const candidate = t.original ?? t.en;
      if (typeof candidate === 'string') {
        return candidate;
      }
    }
  }

  // API Error format (IError)
  if (isApiError(error)) {

    // Try extra.info
    if (error.extra?.info) {
      const info = error.extra.info;
      if (typeof info === 'string') {
        return info;
      }
      if (typeof info === 'object' && info !== null) {
        const message = (info as { message?: string }).message;
        if (typeof message === 'string') {
          return message;
        }
      }
    }

    // String userMessage — last resort before the JSON-stringify fallback
    // (fetch-active-orders threw a JSON blob for { code, userMessage: 'key' }
    // otherwise), still AFTER extra.info to preserve historical precedence.
    if (typeof error.userMessage === 'string' && error.userMessage) {
      return error.userMessage;
    }
  }

  // If error is a string
  if (typeof error === 'string') {
    return error;
  }

  // Fallback: try to stringify
  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
}

/**
 * User-facing variant of getErrorMessage. `toApiError` output and
 * `makeTokenError` output are Error INSTANCES carrying a friendly `userMessage`
 * — but getErrorMessage's isStandardError branch returns the developer-oriented
 * `message` ("Recurly: hard decline — …") before ever reading it. Prefer the
 * attached userMessage when present; fall back to getErrorMessage otherwise.
 * Use this for text shown to the user, getErrorMessage for logs/Sentry.
 */
export function getUserFacingErrorMessage(error: unknown): string {
  return getAttachedUserMessage(error) ?? getErrorMessage(error);
}

/**
 * Strict variant: ONLY the curated userMessage attached to the error, or
 * undefined. Use for error surfaces where a raw Error.message would leak
 * internals (TypeError, network faults, SDK noise) — callers fall back to
 * their own fixed generic copy instead.
 */
export function getAttachedUserMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('userMessage' in error)) {
    return undefined;
  }
  const userMessage = (error as { userMessage?: unknown }).userMessage;
  if (typeof userMessage === 'object' && userMessage !== null) {
    const localized = userMessage as { original?: unknown; en?: unknown };
    const candidate = localized.original ?? localized.en;
    if (typeof candidate === 'string' && candidate) {
      return candidate;
    }
  }
  if (typeof userMessage === 'string' && userMessage) {
    return userMessage;
  }
  return undefined;
}

/**
 * Like getAttachedUserMessage, but returns the localized object unflattened so
 * `t()` can resolve it per-language (handleTranslation reads key[lang] ??
 * original ?? en). getAttachedUserMessage collapses to original/en, which
 * discards the es/pt copies that e.g. useRecurlyToken attaches.
 */
export function getAttachedLocalizedUserMessage(error: unknown): TranslationType | string | undefined {
  if (typeof error !== 'object' || error === null || !('userMessage' in error)) {
    return undefined;
  }
  const userMessage = (error as { userMessage?: unknown }).userMessage;
  if (typeof userMessage === 'object' && userMessage !== null) {
    const localized = userMessage as { original?: unknown; en?: unknown };
    const candidate = localized.original ?? localized.en;
    if (typeof candidate === 'string' && candidate) {
      return userMessage as TranslationType;
    }
  }
  if (typeof userMessage === 'string' && userMessage) {
    return userMessage;
  }
  return undefined;
}
